import { useState, useEffect } from 'react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import ScrollProgress from '../../components/storefront/ScrollProgress';
import BenefitCards from '../../components/storefront/BenefitCards';
import { useCartCtx } from '../../App';
import { useCurrency } from '../../hooks/useCurrency';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import styles from './GlaceProduct.module.css';

const ACCENT = '#5b8fb0';

const BENEFITS = [
  {
    icon: 'snowflake',
    title: 'Reusable & Refreezable',
    summary: 'No melting gel, no waste.',
    more: 'Each pair lives in your freezer and is good for hundreds of uses. Rinse, dry, and refreeze — the cold ritual that never runs out.',
  },
  {
    icon: 'clock',
    title: '10-Minute De-Puff',
    summary: 'Real cryo-cold, fast.',
    more: 'Press them on for ten minutes and let genuine cold drain the puff, calm redness, and wake your face up — no gimmicks, just temperature.',
  },
  {
    icon: 'ice',
    title: 'Gel-Free Cold Therapy',
    summary: 'Oversized coverage.',
    more: 'Big enough to wrap the whole under-eye and upper cheek. Flexible even when frozen — never rock-hard against your skin.',
  },
];

const PatchSVG = () => (
  <svg viewBox="0 0 120 70" width="120" height="70" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="glacePatch" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#f3fbff" />
        <stop offset="1" stopColor="#9fd2ff" />
      </linearGradient>
    </defs>
    <path
      d="M10 38 Q 18 14 50 14 Q 92 12 108 30 Q 116 42 96 52 Q 60 66 30 58 Q 8 52 10 38 Z"
      fill="url(#glacePatch)"
      stroke="#eaf7ff"
      strokeWidth="1.5"
    />
    <path d="M24 36 Q 50 26 92 34" stroke="#ffffff" strokeOpacity=".7" strokeWidth="1.4" fill="none" />
  </svg>
);

export default function GlaceProduct() {
  const { fmt } = useCurrency();
  const { addItem } = useCartCtx();
  const [product, setProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    api.get('/products/glace')
      .then((r) => {
        setProduct(r.data);
        setSelectedVariant(r.data.variants[0]);
      })
      .catch(() => toast.error('Could not load GLACÉ.'));
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleAdd = () => {
    if (!selectedVariant) return;
    addItem(selectedVariant, product, qty);
    toast.success('Added to bag');
  };

  if (product === null) {
    return <div className={styles.loading}>Loading GLACÉ…</div>;
  }

  const onSale =
    selectedVariant &&
    selectedVariant.comparePriceInCents > selectedVariant.priceInCents;

  return (
    <div className={styles.page}>
      <ScrollProgress color={ACCENT} />
      <Navbar />

      {/* ============ HERO ============ */}
      <header className={styles.hero}>
        <div className={styles.heroFog} />
        <div className={styles.crystalWrap}>
          <div className={styles.crystalGlow} />
          <svg className={styles.crystal} viewBox="0 0 420 320" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="ice" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#f3fbff" />
                <stop offset=".45" stopColor="#bfe6ff" />
                <stop offset="1" stopColor="#5fa3e0" />
              </linearGradient>
              <radialGradient id="core" cx="50%" cy="50%" r="55%">
                <stop offset="0" stopColor="#ffffff" />
                <stop offset=".4" stopColor="#e2f5ff" />
                <stop offset="1" stopColor="#9fd2ff" stopOpacity="0" />
              </radialGradient>
              <filter id="blur"><feGaussianBlur stdDeviation="3" /></filter>
              <mask id="maskHoles">
                <rect width="420" height="320" fill="#fff" />
                <ellipse cx="140" cy="150" rx="44" ry="30" fill="#000" transform="rotate(-10 140 150)" />
                <ellipse cx="280" cy="150" rx="44" ry="30" fill="#000" transform="rotate(10 280 150)" />
              </mask>
            </defs>

            <ellipse cx="210" cy="150" rx="180" ry="100" fill="url(#core)" filter="url(#blur)" />

            <g mask="url(#maskHoles)">
              <path
                d="M210 96 C 250 70 300 64 348 78 C 392 92 410 120 404 150 C 414 176 392 214 350 224 C 320 232 292 214 280 188 C 262 210 244 214 226 198 L210 176 L194 198 C 176 214 158 210 140 188 C 128 214 100 232 70 224 C 28 214 6 176 16 150 C 10 120 28 92 72 78 C 120 64 170 70 210 96 Z"
                fill="url(#ice)"
                stroke="#eaf7ff"
                strokeWidth="2.4"
                opacity=".95"
              />
              <g stroke="#ffffff" strokeOpacity=".5" strokeWidth="1.5" fill="none">
                <path d="M210 96 L210 176" />
                <path d="M348 78 L280 150 L350 224" />
                <path d="M404 150 L280 150 L210 96" />
                <path d="M72 78 L140 150 L70 224" />
                <path d="M16 150 L140 150 L210 96" />
                <path d="M280 188 L280 150 M140 188 L140 150" />
              </g>
              <g stroke="#ffffff" strokeWidth="2.2" fill="none" strokeLinecap="round" opacity=".9" filter="url(#blur)">
                <path d="M210 100 L210 174" />
                <path d="M340 90 L300 120 L330 180 L300 210" />
                <path d="M80 90 L120 120 L90 180 L120 210" />
              </g>
            </g>

            <g stroke="#eaf7ff" strokeWidth="2" fill="rgba(174,227,255,.18)" strokeLinejoin="round">
              <path d="M210 92 L200 40 L210 14 L220 40 Z" />
              <path d="M260 92 L268 52 L284 36 L278 64 Z" />
              <path d="M160 92 L152 52 L136 36 L142 64 Z" />
            </g>
            <g fill="#ffffff" opacity=".85">
              <circle cx="210" cy="20" r="4" />
              <circle cx="286" cy="34" r="3" />
              <circle cx="134" cy="34" r="3" />
            </g>
            <path d="M210 96 L240 110 L210 150 L180 110 Z" fill="#ffffff" opacity=".22" />
          </svg>
        </div>

        <div className={styles.heroTag}>
          <h1>Freeze. Apply. <b>Awaken.</b></h1>
          <p>Oversized Cryo Eye Patches</p>
          <button type="button" className={styles.heroCta} onClick={() => scrollTo('shop')}>
            Shop the Freeze →
          </button>
        </div>
      </header>

      {/* ============ VALUE STRIP ============ */}
      <div className={`${styles.strip} ${styles.mono}`}>
        <div>
          <svg viewBox="0 0 24 24" fill="none" stroke="#aee3ff" strokeWidth="2"><path d="M12 2v20M5 7l14 10M19 7L5 17" /></svg>
          Reusable &amp; Refreezable
        </div>
        <div>
          <svg viewBox="0 0 24 24" fill="none" stroke="#aee3ff" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
          10-Min De-Puff
        </div>
        <div>
          <svg viewBox="0 0 24 24" fill="none" stroke="#aee3ff" strokeWidth="2"><path d="M12 21s-7-4.5-7-10a7 7 0 0114 0c0 5.5-7 10-7 10z" /></svg>
          Gel-Free Cold Therapy
        </div>
      </div>

      {/* ============ PITCH ============ */}
      <section className={styles.pitch}>
        <div className={styles.wrap}>
          <div className={`${styles.eyebrow} ${styles.mono}`}>The Cold Ritual</div>
          <h2>Genuinely oversized. <b>Seriously cold.</b></h2>
          <p>
            GLACÉ patches are big enough to wrap the whole under-eye and upper cheek. Keep
            them in the freezer, press them on for ten minutes, and let real cryo-cold drain
            the puff, calm the redness, and wake your face up. No melting gel, no waste — just
            refreeze and repeat.
          </p>
        </div>
      </section>

      {/* ============ SHOP ============ */}
      <section className={styles.shop} id="shop">
        <div className={styles.wrap}>
          <div className={styles.shopHead}>
            <div className={`${styles.eyebrow} ${styles.mono}`}>Build Your Freeze</div>
            <h2>Choose your <b>cold supply</b></h2>
            <p>Each set is a pair of oversized reusable cryo patches. Stock up — they live in the freezer.</p>
          </div>

          <div className={styles.panel}>
            <div className={styles.patchVis}>
              <PatchSVG />
            </div>

            <div className={styles.panelInfo}>
              <div className={`${styles.qty} ${styles.mono}`}>Cryo Patches</div>
              <h3>{product.name}</h3>
              {product.description && <div className={styles.pairs}>{product.description}</div>}

              <div className={styles.variants}>
                {product.variants.map((v) => {
                  const active = selectedVariant && selectedVariant.id === v.id;
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => { setSelectedVariant(v); setQty(1); }}
                      className={`${styles.variant} ${active ? styles.variantActive : ''}`}
                    >
                      <span className={styles.variantName}>{v.name}</span>
                      <span className={styles.variantPrice}>{fmt(v.priceInCents)}</span>
                    </button>
                  );
                })}
              </div>

              {selectedVariant && (
                <>
                  <div className={styles.price}>
                    <span className={styles.now}>{fmt(selectedVariant.priceInCents)}</span>
                    {onSale && (
                      <span className={styles.was}>{fmt(selectedVariant.comparePriceInCents)}</span>
                    )}
                  </div>
                  <div className={styles.stock}>
                    {selectedVariant.inventory > 0
                      ? `${selectedVariant.inventory} in stock`
                      : 'Out of stock'}
                  </div>

                  <div className={styles.stepper}>
                    <button
                      type="button"
                      onClick={() => setQty((q) => Math.max(1, q - 1))}
                      disabled={qty <= 1}
                      aria-label="Decrease quantity"
                    >
                      −
                    </button>
                    <span>{qty}</span>
                    <button
                      type="button"
                      onClick={() => setQty((q) => q + 1)}
                      aria-label="Increase quantity"
                    >
                      +
                    </button>
                  </div>

                  <button
                    type="button"
                    className={`${styles.add} ${styles.mono}`}
                    onClick={handleAdd}
                    disabled={selectedVariant.inventory < 1}
                  >
                    {selectedVariant.inventory < 1 ? 'Out of Stock' : 'Add to Bag'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section className={styles.how} id="how">
        <div className={styles.wrap}>
          <h2>Three steps to a <b>cold awakening</b></h2>
          <div className={styles.steps}>
            <div className={styles.step}>
              <div className={styles.stepNum}>1</div>
              <h4>Freeze</h4>
              <p>Pop your GLACÉ patches in the freezer for at least two hours. They stay flexible — never rock-hard.</p>
            </div>
            <div className={styles.step}>
              <div className={styles.stepNum}>2</div>
              <h4>Apply</h4>
              <p>Press the oversized patches under your eyes and across the cheeks. Lie back for ten cold, calming minutes.</p>
            </div>
            <div className={styles.step}>
              <div className={styles.stepNum}>3</div>
              <h4>Refreeze</h4>
              <p>Rinse, dry, and return them to the freezer. Each pair is good for hundreds of uses.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ BENEFITS ============ */}
      <section className={styles.benefits}>
        <div className={styles.wrap}>
          <h2>Why it <b>works</b></h2>
          <BenefitCards items={BENEFITS} accent={ACCENT} />
        </div>
      </section>

      {/* snow drift over footer */}
      <div className={styles.driftWrap}>
        <svg className={styles.drift} viewBox="0 0 1440 120" preserveAspectRatio="none">
          <path d="M0 80 C 180 30 360 110 540 70 C 720 30 900 110 1080 70 C 1260 30 1440 90 1440 90 L1440 0 L0 0 Z" fill="#0e2547" />
          <path d="M0 90 C 200 50 380 120 560 85 C 760 45 940 120 1120 85 C 1300 55 1440 100 1440 100 L1440 120 L0 120 Z" fill="#ffffff" opacity=".9" />
        </svg>
      </div>

      <Footer />

      {/* big snow bank at very bottom */}
      <div className={styles.snowbank}>
        <svg viewBox="0 0 1440 130" preserveAspectRatio="none">
          <path d="M0 60 C 160 20 320 90 480 55 C 640 20 800 95 960 60 C 1120 25 1280 85 1440 55 L1440 130 L0 130 Z" fill="#ffffff" />
          <path d="M0 95 C 200 70 360 120 540 100 C 740 78 900 125 1080 102 C 1260 80 1440 115 1440 115 L1440 130 L0 130 Z" fill="#dcefff" />
        </svg>
      </div>

      {/* fixed snow piled at edges */}
      <svg className={`${styles.edgeSnow} ${styles.edgeSnowLeft}`} viewBox="0 0 90 800" preserveAspectRatio="none">
        <path d="M0 0 C 40 60 10 140 50 210 C 80 270 20 340 60 420 C 90 490 25 560 55 650 C 80 720 30 770 40 800 L0 800 Z" fill="#ffffff" opacity=".92" />
        <path d="M0 0 C 25 50 5 120 30 190 C 55 260 10 330 35 410 C 60 480 15 550 30 640 C 50 720 15 760 20 800 L0 800 Z" fill="#dcefff" />
      </svg>
      <svg className={`${styles.edgeSnow} ${styles.edgeSnowRight}`} viewBox="0 0 90 800" preserveAspectRatio="none">
        <path d="M90 0 C 50 60 80 140 40 210 C 10 270 70 340 30 420 C 0 490 65 560 35 650 C 10 720 60 770 50 800 L90 800 Z" fill="#ffffff" opacity=".92" />
        <path d="M90 0 C 65 50 85 120 60 190 C 35 260 80 330 55 410 C 30 480 75 550 60 640 C 40 720 75 760 70 800 L90 800 Z" fill="#dcefff" />
      </svg>
    </div>
  );
}
